import { Paperclip, SquareCheck } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import NotesCheckbox from '@/components/notes/notes-checkbox';
import { getAuthUser } from '@/lib/api/token-store';
import { createTodoItemId, detectTodoTrigger } from '@/lib/notes/notes-helpers';
import { cn } from '@/lib/utils';

// Per-user, per-device composer draft (composer drafts stay device-local by convention —
// only saved blocks sync). Survives a restart or an accidental Esc.
const DRAFT_STORAGE_PREFIX = 'healo-notes-composer-draft:';

function draftKey() {
  const uid = String(getAuthUser()?.id || '').trim();
  return uid ? `${DRAFT_STORAGE_PREFIX}${uid}` : '';
}

function loadDraft() {
  try {
    const key = draftKey();
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      mode: parsed?.mode === 'todos' ? 'todos' : 'note',
      text: String(parsed?.text || ''),
      pendingTodos: (Array.isArray(parsed?.pendingTodos) ? parsed.pendingTodos : [])
        .map((row) => ({ id: String(row?.id || ''), label: String(row?.label || '') }))
        .filter((row) => row.id && row.label),
    };
  } catch {
    return null;
  }
}

function saveDraft(draft) {
  try {
    const key = draftKey();
    if (!key) return;
    const empty = !draft.text && draft.pendingTodos.length === 0 && draft.mode === 'note';
    if (empty) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    /* draft persistence is best-effort */
  }
}

function ChipButton({ icon: Icon, label, active, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-[7px] border border-border px-2 py-1 text-[11.5px] font-bold text-muted-foreground transition-colors hover:bg-muted',
        active && 'border-primary/35 bg-primary/10 text-primary',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <Icon className="size-[11px]" strokeWidth={2.5} aria-hidden />
      {label}
    </button>
  );
}

/**
 * The top-of-window entry card. Two modes:
 * - note: Enter saves the text as a note block (Shift+Enter for a newline).
 * - todos: rows accumulate above the input (Enter adds one, Backspace on an empty input
 *   pulls the last row back for editing); Enter on an empty input saves the checklist.
 * Typing "[]" at the start of a fresh line — or the To-do chip — switches note → todos.
 */
const NotesComposer = forwardRef(function NotesComposer(
  { disabled, onSaveNote, onSaveTodos, onFiles, onPasteFiles },
  ref,
) {
  const [mode, setMode] = useState('note');
  const [text, setText] = useState('');
  const [pendingTodos, setPendingTodos] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const restoredRef = useRef(false);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    /** Esc hook: leaves todo mode (discarding pending rows); true when it consumed the key. */
    cancelTodoMode: () => {
      if (mode !== 'todos') return false;
      setMode('note');
      setPendingTodos([]);
      setText('');
      return true;
    },
  }));

  // Restore the per-user draft once (StrictMode-safe via ref), then persist on every edit.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const draft = loadDraft();
    if (draft) {
      setMode(draft.mode);
      setText(draft.text);
      setPendingTodos(draft.pendingTodos);
    }
    textareaRef.current?.focus();
  }, []);
  useEffect(() => {
    if (!restoredRef.current) return;
    saveDraft({ mode, text, pendingTodos });
  }, [mode, text, pendingTodos]);

  const enterTodoMode = (seedText) => {
    setMode('todos');
    setText(String(seedText ?? text));
    textareaRef.current?.focus();
  };

  const handleChange = (event) => {
    const next = event.target.value;
    if (mode === 'note') {
      const trigger = detectTodoTrigger(next);
      if (trigger) {
        enterTodoMode(trigger.remainder);
        return;
      }
    }
    setText(next);
  };

  const submit = () => {
    if (disabled) return;
    if (mode === 'note') {
      const trimmed = text.trim();
      if (!trimmed) return;
      onSaveNote?.(trimmed);
      setText('');
      return;
    }
    const trimmed = text.trim();
    if (trimmed) {
      setPendingTodos((rows) => [...rows, { id: createTodoItemId(), label: trimmed }]);
      setText('');
      return;
    }
    // Enter on an empty row finishes the group (a rowless group just leaves the mode).
    if (pendingTodos.length > 0) {
      onSaveTodos?.(pendingTodos.map((row) => ({ ...row, done: false })));
    }
    setMode('note');
    setPendingTodos([]);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === 'Backspace' && mode === 'todos' && text === '' && pendingTodos.length > 0) {
      // Pull the last committed row back into the input for editing.
      event.preventDefault();
      const last = pendingTodos[pendingTodos.length - 1];
      setPendingTodos((rows) => rows.slice(0, -1));
      setText(last.label);
    }
  };

  const handlePaste = (event) => {
    onPasteFiles?.(event);
  };

  const handleFilePick = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length) onFiles?.(files);
  };

  return (
    <div className="mx-3 mb-1 mt-2.5 flex shrink-0 flex-col gap-2 rounded-[10px] border border-border bg-card2 px-2.5 py-[9px]">
      {mode === 'todos' && pendingTodos.length > 0 ? (
        <div className="flex flex-col gap-1">
          {pendingTodos.map((row) => (
            <div key={row.id} className="flex items-start gap-[9px]">
              <NotesCheckbox checked={false} disabled label={row.label} />
              <span className="min-w-0 flex-1 text-[13px] leading-snug">{row.label}</span>
            </div>
          ))}
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        rows={1}
        value={text}
        disabled={disabled}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={
          mode === 'todos'
            ? pendingTodos.length > 0
              ? 'Next to-do — Enter on an empty line saves'
              : 'First to-do…'
            : 'Jot a note…'
        }
        className="max-h-40 min-h-[22px] w-full resize-none border-0 bg-transparent p-0 text-[13px] leading-relaxed outline-none placeholder:text-muted-foreground disabled:opacity-60"
      />
      <div className="flex items-center gap-1">
        <ChipButton
          icon={SquareCheck}
          label="To-do"
          active={mode === 'todos'}
          disabled={disabled}
          onClick={() => (mode === 'todos' ? submit() : enterTodoMode())}
        />
        <ChipButton
          icon={Paperclip}
          label="File"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
        />
        <span className="ml-auto text-[10.5px] text-muted-foreground/75">
          Enter saves · drop files anywhere
        </span>
      </div>
      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFilePick} />
    </div>
  );
});

export default NotesComposer;
